import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/App";
import axios from "axios";
import {
  FileCheck, Mail, ChevronUp, ChevronDown, Loader2,
  CheckSquare, Square, Plus, Trash2, Shield, UserCheck, AlertTriangle
} from "lucide-react";

const CLIENT_COLORS = ["#3b82f6", "#e11d48", "#d97706", "#7c3aed"];
const ADVISOR_COLOR = "#16a34a";

const splitName = (fullName) => {
  if (!fullName) return { first: "", last: "" };
  const parts = fullName.trim().split(/\s+/);
  return { first: parts[0] || "", last: parts.slice(1).join(" ") || "" };
};

const ordinalLabel = (n) => {
  if (n === 1) return "1st";
  if (n === 2) return "2nd";
  if (n === 3) return "3rd";
  return `${n}th`;
};

const DocuSignAgreementCard = ({ proposal, token, user, onChange, docusignConnected }) => {
  const [documents, setDocuments] = useState([]);
  const [mappingsCache, setMappingsCache] = useState({});
  const [loadingDocs, setLoadingDocs] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };
  const config = proposal.docusign_config || {};

  const updateConfig = (patch) => {
    onChange({ ...config, ...patch });
  };

  // Load user's DocuSign documents
  useEffect(() => {
    const load = async () => {
      // Skip loading if DocuSign isn't connected
      if (docusignConnected === false) {
        setLoadingDocs(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/docusign/documents`, { headers, withCredentials: true });
        setDocuments(res.data);
      } catch (err) {
        // Silently fail - user may not have DocuSign connected
        console.log("DocuSign documents not available");
      }
      finally { setLoadingDocs(false); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docusignConnected]);

  // Load mappings when agreement is selected
  useEffect(() => {
    if (!config.agreement_doc_id || mappingsCache[config.agreement_doc_id]) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/docusign/documents/${config.agreement_doc_id}/mappings`, { headers, withCredentials: true });
        setMappingsCache(prev => ({ ...prev, [config.agreement_doc_id]: res.data }));
      } catch { /* silently fail */ }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.agreement_doc_id]);

  const agreements = documents.filter(d => d.doc_type === "agreement");
  const supportingDocs = documents.filter(d => d.doc_type === "supporting");
  const selectedMappings = config.agreement_doc_id ? mappingsCache[config.agreement_doc_id] : null;
  const agreementSigners = selectedMappings?.signers || [];

  // Auto-select supporting docs with bundle_with_agreement when agreement changes (only on initial selection)
  useEffect(() => {
    if (!config.agreement_doc_id) return;
    
    // Only auto-add bundled docs if we haven't done it yet for this agreement
    // Use a flag to track this per agreement
    if (config._bundledDocsPopulated === config.agreement_doc_id) return;
    
    // Get docs that should always be bundled
    const bundledDocIds = supportingDocs
      .filter(d => d.bundle_with_agreement)
      .map(d => d.doc_id);
    
    if (bundledDocIds.length === 0) return;
    
    // Merge with existing selections (don't remove manually added ones)
    const currentSelected = config.supporting_doc_ids || [];
    const merged = [...new Set([...currentSelected, ...bundledDocIds])];
    
    updateConfig({ 
      supporting_doc_ids: merged,
      _bundledDocsPopulated: config.agreement_doc_id 
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.agreement_doc_id]);

  // Pre-populate clients + advisor + additional roles when agreement changes
  useEffect(() => {
    if (!agreementSigners.length || config._signersPrepopulated === config.agreement_doc_id) return;
    const { first, last } = splitName(proposal.prospect_name);

    // Clients from agreement signers with type "client"
    const agreementClients = agreementSigners.filter(s => s.type === "client");
    const clientSignersList = agreementClients.map((s, i) => ({
      signer_id: s.id,
      label: i === 0 ? "Client 1 (Primary)" : `Client ${i + 1}`,
      role: "client",
      first_name: i === 0 ? first : "",
      last_name: i === 0 ? last : "",
      email: i === 0 ? (proposal.prospect_email || "") : "",
      order: i + 1,
    }));

    // Additional roles from agreement signers with type "additional"
    const agreementRoles = agreementSigners.filter(s => s.type === "additional");
    const additionalSignersList = agreementRoles.map(s => ({
      id: s.id,
      role_title: s.label,
      role_id: s.id,
      first_name: "",
      last_name: "",
      email: "",
    }));

    const advisorName = splitName(user?.name || user?.full_name || "");
    const advisorSigner = {
      signer_id: "advisor",
      label: "Advisor (You)",
      role: "advisor",
      first_name: advisorName.first,
      last_name: advisorName.last,
      email: user?.email || "",
      order: clientSignersList.length + additionalSignersList.length + 1,
    };

    updateConfig({
      client_signers: clientSignersList,
      additional_signers: additionalSignersList,
      advisor_signer: advisorSigner,
      signing_mode: config.signing_mode || "in_order",
      _signersPrepopulated: config.agreement_doc_id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementSigners, config.agreement_doc_id]);

  // Keep Client 1 synced with prospect info
  useEffect(() => {
    if (!config.client_signers?.length) return;
    const { first, last } = splitName(proposal.prospect_name);
    const c1 = config.client_signers[0];
    if (c1 && (c1.first_name !== first || c1.last_name !== last || c1.email !== (proposal.prospect_email || ""))) {
      const updated = [...config.client_signers];
      updated[0] = { ...c1, first_name: first, last_name: last, email: proposal.prospect_email || "" };
      updateConfig({ client_signers: updated });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.prospect_name, proposal.prospect_email]);

  const updateClientSigner = (signerId, patch) => {
    const updated = (config.client_signers || []).map(s => s.signer_id === signerId ? { ...s, ...patch } : s);
    updateConfig({ client_signers: updated });
  };

  const swapClientOrder = (idx, direction) => {
    const signers = [...(config.client_signers || [])];
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= signers.length) return;
    const tmp = signers[idx].order;
    signers[idx] = { ...signers[idx], order: signers[targetIdx].order };
    signers[targetIdx] = { ...signers[targetIdx], order: tmp };
    signers.sort((a, b) => a.order - b.order);
    updateConfig({ client_signers: signers });
  };

  // Additional signers (compliance, supervisor, etc.)
  const addAdditionalSigner = () => {
    const existing = config.additional_signers || [];
    const newSigner = {
      id: `add_${Date.now()}`,
      role_title: "",
      first_name: "",
      last_name: "",
      email: "",
    };
    updateConfig({ additional_signers: [...existing, newSigner] });
  };

  const updateAdditionalSigner = (id, patch) => {
    const updated = (config.additional_signers || []).map(s => s.id === id ? { ...s, ...patch } : s);
    updateConfig({ additional_signers: updated });
  };

  const removeAdditionalSigner = (id) => {
    updateConfig({ additional_signers: (config.additional_signers || []).filter(s => s.id !== id) });
  };

  const toggleSupportingDoc = (docId) => {
    const current = config.supporting_doc_ids || [];
    const next = current.includes(docId) ? current.filter(id => id !== docId) : [...current, docId];
    updateConfig({ supporting_doc_ids: next });
  };

  const fieldSummary = useMemo(() => {
    if (!selectedMappings?.fields?.length) return null;
    return selectedMappings.fields.length;
  }, [selectedMappings]);

  // Compute total signer count for display
  const totalSigners = (config.client_signers?.length || 0)
    + (config.additional_signers?.length || 0)
    + (config.advisor_signer ? 1 : 0);

  if (loadingDocs) {
    return (
      <Card className="border-stone">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 text-forest animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (agreements.length === 0) {
    return (
      <Card className={`border-stone ${docusignConnected === false ? 'border-amber-300' : ''}`}>
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <FileCheck className="w-5 h-5" />
            DocuSign Agreement
            {docusignConnected === false && (
              <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Not connected</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {docusignConnected === false ? (
            <div className="text-center py-4">
              <FileCheck className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-3">
                Connect DocuSign to automatically send agreements for electronic signature when prospects accept.
              </p>
              <Button asChild variant="outline" size="sm" className="border-forest text-forest hover:bg-forest/5">
                <Link to="/integrations">
                  Connect DocuSign
                </Link>
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No agreements uploaded yet. Go to{" "}
              <Link to="/settings?tab=documents" className="text-forest hover:underline font-medium">
                Settings → DocuSign Documents
              </Link>{" "}
              to upload and map your agreement.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-stone ${docusignConnected === false ? 'border-amber-300' : ''}`} data-testid="docusign-agreement-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-xl flex items-center gap-2">
          <FileCheck className="w-5 h-5" />
          DocuSign Agreement
          {docusignConnected === false && (
            <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Not connected</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        
        {/* DocuSign Disconnected Warning */}
        {docusignConnected === false && (
          <div className="text-center py-4">
            <FileCheck className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-3">
              Connect DocuSign to automatically send agreements for electronic signature when prospects accept.
            </p>
            <Button asChild variant="outline" size="sm" className="border-forest text-forest hover:bg-forest/5">
              <Link to="/integrations">
                Connect DocuSign
              </Link>
            </Button>
          </div>
        )}

        {/* Show agreement selector only when connected */}
        {docusignConnected !== false && (
          <>
        <div>
          <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Agreement</Label>
          <Select
            value={config.agreement_doc_id || ""}
            onValueChange={(v) => updateConfig({
              agreement_doc_id: v,
              client_signers: null,
              additional_signers: [],
              advisor_signer: null,
              _signersPrepopulated: null,
            })}
          >
            <SelectTrigger className="mt-1.5" data-testid="agreement-select">
              <SelectValue placeholder="Select an agreement..." />
            </SelectTrigger>
            <SelectContent>
              {agreements.map(a => (
                <SelectItem key={a.doc_id} value={a.doc_id}>
                  {a.display_name || a.filename}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldSummary && (
            <p className="text-xs text-muted-foreground mt-1.5">
              {fieldSummary} mapped fields &middot; {totalSigners} total signer{totalSigners !== 1 ? "s" : ""}
            </p>
          )}
        </div>

        {/* Client Signers */}
        {config.client_signers?.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Clients</Label>
              {config.client_signers.length > 1 && (
                <Select
                  value={config.signing_mode || "in_order"}
                  onValueChange={(v) => updateConfig({ signing_mode: v })}
                >
                  <SelectTrigger className="h-7 w-[140px] text-xs" data-testid="signing-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_order">Sign in order</SelectItem>
                    <SelectItem value="all_at_once">All at once</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {config.client_signers.map((signer, idx) => {
              const color = CLIENT_COLORS[(signer.signer_id - 1) % CLIENT_COLORS.length];
              return (
                <div
                  key={signer.signer_id}
                  className="rounded-lg border border-stone p-3 space-y-2"
                  style={{ borderLeftColor: color, borderLeftWidth: "3px" }}
                  data-testid={`client-signer-${signer.signer_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: color }}>
                        {signer.order}
                      </span>
                      <span className="text-sm font-medium text-forest">{signer.label}</span>
                      {config.signing_mode === "in_order" && config.client_signers.length > 1 && (
                        <span className="text-[10px] text-muted-foreground">Signs {ordinalLabel(signer.order)}</span>
                      )}
                      {config.signing_mode === "all_at_once" && (
                        <span className="text-[10px] text-muted-foreground">Simultaneous</span>
                      )}
                    </div>
                    {config.signing_mode === "in_order" && config.client_signers.length > 1 && (
                      <div className="flex gap-0.5">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => swapClientOrder(idx, -1)} disabled={idx === 0} data-testid={`order-up-${signer.signer_id}`}>
                          <ChevronUp className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => swapClientOrder(idx, 1)} disabled={idx === config.client_signers.length - 1} data-testid={`order-down-${signer.signer_id}`}>
                          <ChevronDown className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={signer.first_name || ""}
                      onChange={(e) => updateClientSigner(signer.signer_id, { first_name: e.target.value })}
                      placeholder="First"
                      className="h-8 text-xs"
                      data-testid={`client-first-${signer.signer_id}`}
                    />
                    <Input
                      value={signer.last_name || ""}
                      onChange={(e) => updateClientSigner(signer.signer_id, { last_name: e.target.value })}
                      placeholder="Last"
                      className="h-8 text-xs"
                      data-testid={`client-last-${signer.signer_id}`}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <Input
                      type="email"
                      value={signer.email || ""}
                      onChange={(e) => updateClientSigner(signer.signer_id, { email: e.target.value })}
                      placeholder="email@example.com"
                      className="h-8 text-xs"
                      data-testid={`client-email-${signer.signer_id}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Additional Signers (Compliance, Supervisor, etc.) */}
        {config.agreement_doc_id && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Additional Signatures</Label>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-forest hover:text-forest gap-1"
                onClick={addAdditionalSigner}
                data-testid="add-additional-signer"
              >
                <Plus className="w-3 h-3" /> Add
              </Button>
            </div>
            {(!config.additional_signers || config.additional_signers.length === 0) && (
              <p className="text-xs text-muted-foreground">
                Need compliance, supervisor, or other signatures? Click Add above.
              </p>
            )}
            {(config.additional_signers || []).map((signer) => (
              <div
                key={signer.id}
                className="rounded-lg border border-stone p-3 space-y-2"
                style={{ borderLeftColor: "#64748b", borderLeftWidth: "3px" }}
                data-testid={`additional-signer-${signer.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-slate-500" />
                    <Input
                      value={signer.role_title || ""}
                      onChange={(e) => updateAdditionalSigner(signer.id, { role_title: e.target.value })}
                      placeholder="Role (e.g., Compliance Officer)"
                      className="h-7 text-xs border-0 p-0 font-medium text-forest focus-visible:ring-0 bg-transparent"
                      data-testid={`additional-role-${signer.id}`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-red-400 hover:text-red-600"
                    onClick={() => removeAdditionalSigner(signer.id)}
                    data-testid={`remove-additional-${signer.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={signer.first_name || ""}
                    onChange={(e) => updateAdditionalSigner(signer.id, { first_name: e.target.value })}
                    placeholder="First"
                    className="h-8 text-xs"
                    data-testid={`additional-first-${signer.id}`}
                  />
                  <Input
                    value={signer.last_name || ""}
                    onChange={(e) => updateAdditionalSigner(signer.id, { last_name: e.target.value })}
                    placeholder="Last"
                    className="h-8 text-xs"
                    data-testid={`additional-last-${signer.id}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <Input
                    type="email"
                    value={signer.email || ""}
                    onChange={(e) => updateAdditionalSigner(signer.id, { email: e.target.value })}
                    placeholder="email@example.com"
                    className="h-8 text-xs"
                    data-testid={`additional-email-${signer.id}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Advisor (Always signs last) */}
        {config.advisor_signer && (
          <div className="space-y-2">
            <Label className="font-label text-sm uppercase tracking-wider text-muted-foreground">Advisor (Signs Last)</Label>
            <div
              className="rounded-lg border border-stone p-3 space-y-2"
              style={{ borderLeftColor: ADVISOR_COLOR, borderLeftWidth: "3px" }}
              data-testid="advisor-signer-card"
            >
              <div className="flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-forest">
                  {config.advisor_signer.first_name} {config.advisor_signer.last_name}
                </span>
                <span className="text-[10px] text-muted-foreground ml-auto">Always signs last</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground">{config.advisor_signer.email}</span>
              </div>
            </div>
          </div>
        )}

        {/* Supporting Documents - only show when agreement is selected */}
        {config.agreement_doc_id && supportingDocs.length > 0 && (
          <div className="space-y-2">
            {/* Split into bundled (always include) and optional docs */}
            {(() => {
              const bundledDocs = supportingDocs.filter(d => d.bundle_with_agreement);
              const optionalDocs = supportingDocs.filter(d => !d.bundle_with_agreement);
              
              return (
                <>
                  {/* Bundled docs - shown as "Exclude" option */}
                  {bundledDocs.length > 0 && (
                    <div className="space-y-1">
                      <Label className="font-label text-xs uppercase tracking-wider text-muted-foreground">
                        Included by Default
                      </Label>
                      {bundledDocs.map(doc => {
                        const included = (config.supporting_doc_ids || []).includes(doc.doc_id);
                        return (
                          <button
                            key={doc.doc_id}
                            onClick={() => toggleSupportingDoc(doc.doc_id)}
                            className="flex items-center gap-2.5 w-full p-2 rounded-md hover:bg-sand/50 transition-colors text-left"
                            data-testid={`supporting-doc-${doc.doc_id}`}
                          >
                            {included
                              ? <CheckSquare className="w-4 h-4 text-forest shrink-0" />
                              : <Square className="w-4 h-4 text-muted-foreground shrink-0" />
                            }
                            <span className={`text-sm truncate ${included ? "text-forest font-medium" : "text-muted-foreground"}`}>
                              {doc.display_name || doc.filename}
                            </span>
                            <span className={`text-xs ml-auto ${included ? "text-green-600" : "text-amber-600"}`}>
                              ({included ? "included" : "excluded"})
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Optional docs - shown as "Include" option */}
                  {optionalDocs.length > 0 && (
                    <div className="space-y-1">
                      <Label className="font-label text-xs uppercase tracking-wider text-muted-foreground">
                        {bundledDocs.length > 0 ? "Optional Documents" : "Include Supporting Docs"}
                      </Label>
                      {optionalDocs.map(doc => {
                        const checked = (config.supporting_doc_ids || []).includes(doc.doc_id);
                        return (
                          <button
                            key={doc.doc_id}
                            onClick={() => toggleSupportingDoc(doc.doc_id)}
                            className="flex items-center gap-2.5 w-full p-2 rounded-md hover:bg-sand/50 transition-colors text-left"
                            data-testid={`supporting-doc-${doc.doc_id}`}
                          >
                            {checked
                              ? <CheckSquare className="w-4 h-4 text-forest shrink-0" />
                              : <Square className="w-4 h-4 text-muted-foreground shrink-0" />
                            }
                            <span className={`text-sm truncate ${checked ? "text-forest font-medium" : "text-muted-foreground"}`}>
                              {doc.display_name || doc.filename}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
        </>
        )}

        {/* Auto-send Toggle - only show when connected */}
        {docusignConnected !== false && (
          <div className="flex items-center justify-between pt-2 border-t border-stone/50">
            <div>
              <p className="text-sm font-medium text-forest">Auto-send on accept</p>
              <p className="text-xs text-muted-foreground">
                {!config.agreement_doc_id 
                  ? "Select an agreement document first" 
                  : "Send agreement automatically when prospect accepts"}
              </p>
            </div>
            <Switch
              checked={config.auto_send_on_accept || false}
              onCheckedChange={(v) => updateConfig({ auto_send_on_accept: v })}
              disabled={!config.agreement_doc_id}
              data-testid="auto-send-toggle"
            />
          </div>
        )}

      </CardContent>
    </Card>
  );
};

export default DocuSignAgreementCard;
